/**
 * Las rutas que una cuenta del plan `almacen` puede abrir: una lista blanca, gemela de la del rol
 * Drop y por el mismo motivo.
 *
 * ═══ POR QUÉ EXISTE ESTE ARCHIVO ════════════════════════════════════════════════════════════════
 * 🚨 EL FOUNDER PREGUNTÓ LO EXACTO (23-sep-2026): «¿ya tiene los permisos sólo de almacén para que
 *    vea sólo lo que él debe ver?». La respuesta medida era NO: `plan_type` sólo distinguía `drop`,
 *    `basic` y `enterprise`, y los dos proveedores de entonces —Vitalex y Smart Home— eran
 *    `enterprise`. Un proveedor ajeno habría entrado a Automatización, Ads, Contabilidad, Reportes,
 *    Estadísticas, Marketing, POS, Inventario general y WhatsApp.
 *
 *    **No era una fuga de datos** —RLS le muestra sólo su propio negocio, nunca el de otro— pero sí
 *    tres problemas: le entregábamos un ERP que no compró y que no vamos a sostener para él;
 *    «Inventario» lo dejaba ajustar existencias de productos que otros están vendiendo; y sus
 *    encargos seguían mezclados con sus ventas propias.
 *
 * ⭐ LA REGLA ES DEL PLAN Y VIVE EN UN SOLO LUGAR, como la del rol Drop. Esconder módulos uno por
 *    uno deja pasar el que se agregue mañana; con lista blanca, lo nuevo queda afuera solo. Es la
 *    lección que ya costó una vez: el menú del rol Drop era lista blanca pero las RUTAS se cuidaban
 *    por módulo, y 150 de 282 no declaraban ninguno.
 *
 * ═══ QUÉ VE UN ALMACÉN, Y QUÉ NO ════════════════════════════════════════════════════════════════
 * 🪤 UN ALMACÉN NO ES UN DROPSHIPPER AL REVÉS. El dropshipper no crea productos —los importa— y no
 *    toca existencias. El almacén hace exactamente esas dos cosas: **los productos son suyos y el
 *    stock es el dato del que depende que un encargo se pueda despachar**. Por eso acá sí entran
 *    `PRODUCTS_NEW` e `PRODUCTS_INVENTORY`, que en el rol Drop están prohibidos.
 *
 * 🪤 Lo que la base ya protege con RLS no depende de esto: esto es la puerta de la PANTALLA.
 */
import { ROUTES } from '@/config/routes.config';
import { rutaSinTienda } from './rutasDelRolDrop';
import { matchPath } from 'react-router-dom';

export const RUTAS_DEL_ROL_ALMACEN: readonly string[] = [
  ROUTES.HOME,
  // Todas las notificaciones (20-oct-2026): la campana muestra las últimas 20 y su pie lleva acá.
  ROUTES.NOTIFICACIONES,

  /*
   * Sus ventas: las de siempre. Un almacén vende —al revendedor y, si quiere, a su propio
   * cliente—, así que el módulo entra entero, con la venta y sus páginas.
   */
  ROUTES.OPS_VENTAS, ROUTES.OPS_PEDIDOS, ROUTES.OPS_COBRANZAS, ROUTES.OPS_CANCELADAS,
  ROUTES.OPS_ORDER_NEW, ROUTES.OPS_ORDER_DETAIL, ROUTES.OPS_ORDER_ENTREGA,
  ROUTES.OPS_ORDER_DROP, ROUTES.OPS_ORDER_PAGOS,
  /* ⭐ La cola de encargos del almacén: lo que tiene que preparar. Es SU pantalla, no la del drop. */
  ROUTES.OPS_DROP,
  /* Despachar es la mitad de su trabajo. */
  ROUTES.ENTREGAS, ROUTES.OPS_LOGISTICA,
  /* Sus guías de despacho; y si también es courier (North Express), lo que recoge (20261021004300) y sus liquidaciones
     día por día (26-oct-2026: el founder, en NX, «al darle clic me lleva a la página de inicio»: faltaba acá). */
  ROUTES.ENTREGAS_GUIAS, ROUTES.ENTREGAS_POR_RECOGER, ROUTES.ENTREGAS_LIQUIDACIONES,

  /*
   * Sus productos, completos: crear, editar, organizar y ajustar existencias. Acá está la
   * diferencia con el rol Drop.
   */
  ROUTES.PRODUCTS, ROUTES.PRODUCTS_NEW, ROUTES.PRODUCTS_EDIT,
  ROUTES.PRODUCTS_CATEGORIES, ROUTES.PRODUCTS_CATEGORIES_NEW,
  ROUTES.PRODUCTS_CATEGORIES_EDIT, ROUTES.PRODUCTS_CATEGORIES_EDIT_LEGACY,
  ROUTES.PRODUCTS_TAGS,
  ROUTES.PRODUCTS_INVENTORY, ROUTES.PRODUCTS_INVENTORY_PRODUCT,
  ROUTES.PRODUCTS_INVENTORY_PRODUCT_LEGACY,

  /* Mi almacén: de qué agencia sale, qué cobra, sus números, sus incidencias y lo que tiene por cobrar. */
  ROUTES.DROPSHIPPING_SMARTYDROP_ALMACEN,
  /* Su stock con dueño y lugar, las remesas que manda o recibe, y su liquidación (plan de actores §3 y §5). */
  ROUTES.DROPSHIPPING_SMARTYDROP_INVENTARIO,
  ROUTES.DROPSHIPPING_SMARTYDROP_LIQUIDACION,
  // Su billetera: con ella prepaga el fulfillment y el despacho de sus ventas por agencia (20260925194000).
  ROUTES.DROPSHIPPING_SMARTYDROP_SALDO,
  ROUTES.DROPSHIPPING_SMARTYDROP_COURIERS,
  // Transporte, como el drop: las agencias y los motorizados de Smarty Drop, con su cobertura (20260925190000).
  ROUTES.TRANSPORT_AGENCIES, ROUTES.TRANSPORT_AGENCY_COURIER_DETAIL,
  ROUTES.TRANSPORT_MOTORIZADOS, ROUTES.TRANSPORT_MOTOR_COURIER_DETAIL,
  // El papel es de quien imprime (23-oct): el almacén elige el formato y el papel de SU impresora.
  ROUTES.TRANSPORT_ROTULOS,
  ROUTES.DROPSHIPPING_SMARTYDROP_INGRESOS,
  ROUTES.DROPSHIPPING_SMARTYDROP_ENCARGOS,
  /*
   * 🪤 Y EL CATÁLOGO, para que vea lo suyo como lo ven sus clientes. `fn_drop_catalogo` ya le
   *    muestra sus propias filas aunque estén pausadas (`es_mio`), así que es su vidriera y su
   *    control de calidad a la vez.
   */
  ROUTES.DROPSHIPPING_SMARTYDROP,

  /* Sus clientes son los revendedores. */
  ROUTES.CUSTOMERS, ROUTES.CUSTOMER_DETAIL,

  /*
   * Ajustes: su negocio, su gente y los couriers con los que despacha. 🪤 Sin Dominios, Marca,
   * Checkout ni canales de venta: no tiene tienda.
   */
  ROUTES.SETTINGS, ROUTES.SETTINGS_GENERAL, ROUTES.SETTINGS_BUSINESS,
  ROUTES.SETTINGS_USERS, ROUTES.SETTINGS_USERS_ROLES, ROUTES.SETTINGS_USERS_SECURITY,
  ROUTES.SETTINGS_LOCATIONS,
  ROUTES.SETTINGS_INTEGRATIONS, ROUTES.SETTINGS_INTEGRATIONS_SHALOM,
  ROUTES.SETTINGS_INTEGRATIONS_OLVA, ROUTES.SETTINGS_INTEGRATIONS_EVA,
  ROUTES.SETTINGS_SHALOM_AGENCIES,
  ROUTES.SETTINGS_PLAN,
];

/** A dónde va un almacén que abre una ruta que no es suya. */
export const A_DONDE_VA_EL_ALMACEN = ROUTES.HOME;

/**
 * ⭐ EL ALMACÉN ENTRA POR LAS GUÍAS DE DESPACHO (founder, 23-oct-2026: «cuando se entre, para todos los almacenes,
 *    entren a Entregas sí o sí y les aparezca por defecto seleccionado Guías de despacho»). Su trabajo del día es
 *    la guía: preparar, imprimir, entregar. El Inicio del plan almacén es esa pantalla, y el ítem «Entregas» del
 *    menú abre ahí (como el rol Drop abre «Catálogo» en el suyo: `MENU_DEL_ROL_DROP.urls`).
 */
export const A_DONDE_ENTRA_EL_ALMACEN = ROUTES.ENTREGAS_GUIAS;
export const MENU_DEL_ROL_ALMACEN_URLS: Record<string, string> = { entregas: ROUTES.ENTREGAS_GUIAS };

/** ¿Puede una cuenta del plan `almacen` abrir esta URL? */
export function laRutaEsDelRolAlmacen(pathname: string): boolean {
  const ruta = rutaSinTienda(pathname);
  return RUTAS_DEL_ROL_ALMACEN.some((patron) => matchPath({ path: patron, end: true }, ruta) !== null);
}
